import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { logger } from "@/lib/logger";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        // Handle stale/invalid tokens
        if (error) {
          await supabase.auth.signOut();
          setSession(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        
        if (session?.user) {
          try {
            // CRITICAL: Always fetch fresh onboarding status from database
            // This prevents users from bypassing onboarding by navigating directly to protected routes
            const { data, error: profileError } = await supabase
              .from("profiles")
              .select("onboarding_completed")
              .eq("user_id", session.user.id)
              .maybeSingle();
            
            // If profile query fails due to auth, sign out
            if (profileError && (profileError.message?.includes("JWT") || profileError.message?.includes("not exist"))) {
              await supabase.auth.signOut();
              setSession(null);
              setLoading(false);
              return;
            }
            
          // User must complete ALL 7 onboarding steps (start -> trust) before onboarding_completed is true
          setOnboardingComplete(data?.onboarding_completed === true);
        } catch (err) {
          logger.error("Error checking onboarding status:", err);
          setOnboardingComplete(false);
        }
        }
        
        setLoading(false);
      } catch (err) {
        await supabase.auth.signOut();
        setSession(null);
        setLoading(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Synchronous state update only
      setSession(session);
      
      // Defer Supabase calls to avoid deadlocks per best practices
      if (session?.user) {
        setTimeout(async () => {
          const { data } = await supabase
            .from("profiles")
            .select("onboarding_completed")
            .eq("user_id", session.user!.id)
            .maybeSingle();
          setOnboardingComplete(data?.onboarding_completed === true);
          setLoading(false);
        }, 0);
      } else {
        setOnboardingComplete(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Final verification to avoid race conditions when navigating right after completing onboarding
  useEffect(() => {
    if (!session || onboardingComplete) return;
    const isOnboardingRoute = location.pathname.startsWith("/onboarding");
    if (isOnboardingRoute || verifying) return;

    setVerifying(true);
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("user_id", session.user.id)
        .maybeSingle();
      setOnboardingComplete(data?.onboarding_completed === true);
      setVerifying(false);
    })();
  }, [session, onboardingComplete, location.pathname, verifying]);
  if (loading || verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  // CRITICAL ONBOARDING CHECK:
  // Users must complete ALL 7 onboarding steps before accessing any non-onboarding routes
  // Steps: start -> looking-for -> details -> profile -> lifestyle -> final -> trust
  // Only the final "trust" step sets onboarding_completed to true
  const isOnboardingRoute = location.pathname.startsWith("/onboarding");
  const justCompleted = (location.state as any)?.justCompletedOnboarding === true;
  
  // Block access to all non-onboarding routes if onboarding is incomplete
  if (!onboardingComplete && !isOnboardingRoute && !justCompleted) {
    return <Navigate to="/onboarding/start" replace />;
  }

  return <>{children}</>;
};
